import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector, batch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import findIndex from 'lodash/findIndex'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import axios from 'axios'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { v4 as uuidv4 } from 'uuid'
import { useSessionStorage } from 'web-api-hooks'
import CssBaseline from '@material-ui/core/CssBaseline'
import { createACardTheme } from '../components/CreateACard/themeStyles'
import AppHelmet from '../components/CreateACard/AppHelmet'
import handleAddressVerificationResponse from '../utils/addressVerification'
import { useQuery } from '../utils/customHooks'
import Layout from '../components/CreateACard/Layout'
import {
    addCustomCard,
    addToCartRequest,
    clearResetCacAppRequest,
    duplicateCustomCardRequest,
    presetCustomCard,
    putCustomCardAddressValidationResult,
    removeCustomCardRequest,
    requestNextStep,
    resetCacApp,
    resetCustomCard,
    resetEditCustomCardRequest,
    saveCustomCards,
    setAddingToCart,
    setCacAvailableCardNetworks,
    setCacCardNetwork,
    setCacConfig,
    setCacOccasionSlug,
    setCartErrors,
    updateCustomCard,
} from '../actions/createACard'
import {
    pushBuildACardAddGiftToCartToDataLayer,
    pushBuildACardProductViewToDataLayer,
} from '../utils/dataLayer'

export const STEP_LANDING = {
    name: 'landing',
    route: '/build-a-card/',
    pageTitle: 'Personalized Gift Cards - Custom Gift Cards',
    pageDescription:
        'Build a personalized gift card with a custom photo and message. Choose Visa or Mastercard and order custom gift cards easily with Gift Card Granny.',
}
export const STEP_CARRIER = {
    name: 'carrier',
    route: '/build-a-card/carrier/',
    pageTitle: 'Carrier Personalization Options',
    pageDescription:
        'Build your carrier for your customized Visa or Mastercard gift card from Gift Card Granny.',
}
export const STEP_REVIEW = {
    name: 'review',
    route: '/build-a-card/review/',
    pageTitle: 'Review your Build-a-Cards',
    pageDescription:
        'Review your custom Visa or Mastercard gift card(s) and carrier(s) and add them to your Gift Card Granny cart.',
}
export const steps = [STEP_LANDING, STEP_CARRIER, STEP_REVIEW]

export const DEFAULT_BACKGROUND = {
    hex: '#FfFfFf',
    rgb: { r: 256, g: 256, b: 256, a: 1 },
}
export const MASTERCARD = 'mastercard'
export const VISA = 'visa'
export const DEFAULT_DESCRIPTOR_COLOR = 'white'

export const DEFAULT_AMOUNT = 75
export const MIN_LOAD_VALUE = 10
export const MAX_LOAD_VALUE = 500
export const CLOUDINARY_NAME = 'gift-card-granny'

export const MASTERCARD_OVERLAY_ID =
    'GCG:card_overlays:Mastercard_GCG_Number_jghh12'
export const VISA_OVERLAY_ID = 'GCG:card_overlays:Visa_GCG_Number_z4ezjh'

export const onChangeCustomCard = (dispatch, action, state, path, value) => {
    let newState = Object.assign({}, state)
    newState[path] = value
    dispatch(action(newState))
}

export const getNetworkOverlayImageId = (cardNetworkName) => {
    return cardNetworkName === 'Visa' ? VISA_OVERLAY_ID : MASTERCARD_OVERLAY_ID
}

const CreateACard = (props) => {
    const children = props.children

    const history = useHistory()
    const location = useLocation()
    const query = useQuery()
    const dispatch = useDispatch()

    // customization props
    const buildACardConfig = props.buildACardConfig
    const baseUrl = props.baseUrl
    const brandLogoUrl = props.brandLogoUrl
    const availableCardNetworks = props.availableCardNetworks
    const imageUploadUrl = props.imageUploadUrl
    const faqUrl = props.faqUrl
    const addToCartUrl = props.addToCartUrl
    const addToCartCallback = props.addToCartCallback
    const addToCart = props.addToCart
    const addToCartErrorHandler = props.addToCartErrorHandler
    const footerContent = props.footerContent
    const carrierImageUrl = props.carrierImageUrl
    const carrierImageWithCardUrl = props.carrierImageWithCardUrl
    const includeShipping = props.includeShipping
    const imageGuidelinesUrl = props.imageGuidelinesUrl
    const siteName = props.siteName
    const availableShippingMethods = props.availableShippingMethods

    // What step in the flow the user is on.
    const [step, setStep] = useSessionStorage('step', STEP_LANDING)
    const getNextStep = useCallback(() => steps[findIndex(steps, step) + 1], [
        step,
    ])
    const getPreviousStep = () => steps[findIndex(steps, step) - 1]
    const getStepByRoute = (route) => {
        if (steps.find((el) => el.route === route))
            return steps.find((el) => el.route === route)
        return STEP_LANDING
    }

    const addressValidationResponse = useSelector(
        (state) => state.createACard.addressValidationResponse
    )

    const cards = useSelector((state) => state.createACard.cards)
    const customCard = useSelector((state) => state.createACard.customCard)
    const nextStepRequested = useSelector(
        (state) => state.createACard.nextStepRequested
    )
    const resetAppRequested = useSelector(
        (state) => state.createACard.resetAppRequested
    )
    const clearCards = useSelector((state) => state.createACard.clearCards)
    const addToCartRequested = useSelector(
        (state) => state.createACard.addToCartRequested
    )
    const editCardRequest = useSelector(
        (state) => state.createACard.editCardRequest
    )
    const removeCardId = useSelector((state) => state.createACard.removeCardId)
    const duplicateCardId = useSelector(
        (state) => state.createACard.duplicateCardId
    )
    const cardNetwork = useSelector((state) => state.createACard.cardNetwork)
    const occasionSlug = useSelector((state) => state.createACard.occasionSlug)
    const stepByRoute = getStepByRoute(location.pathname)

    // Update custom card properties by path and value
    const handleUpdateCustomCard = useCallback(
        (path, value) => {
            onChangeCustomCard(
                dispatch,
                updateCustomCard,
                customCard,
                path,
                value
            )
        },
        [dispatch, customCard]
    )
    const setAmount = (amount) => {
        handleUpdateCustomCard('amount', amount)
    }

    useEffect(() => {
        const validateAndSetDenomination = (denomination) => {
            const parsedDenomination = parseInt(denomination)
            isNaN(parsedDenomination) ||
            parsedDenomination < MIN_LOAD_VALUE ||
            parsedDenomination > MAX_LOAD_VALUE
                ? setAmount(DEFAULT_AMOUNT)
                : setAmount(parsedDenomination)
        }

        if (isEqual(stepByRoute, STEP_LANDING)) {
            dispatch(setCacOccasionSlug(buildACardConfig.slug))
            // Validate and set initial denomination from denomination query param
            validateAndSetDenomination(query.get('denom'))
            // Set initial card network from network query param
            if (query.get('network') === MASTERCARD) {
                dispatch(setCacCardNetwork(availableCardNetworks[1]))
                pushBuildACardProductViewToDataLayer(
                    availableCardNetworks[1].name
                )
            } else {
                dispatch(setCacCardNetwork(availableCardNetworks[0]))
                pushBuildACardProductViewToDataLayer(
                    availableCardNetworks[0].name
                )
            }
        }

        /*
            We only want the network or denom query param to set cardNetwork & amount state values on mount.
            An empty useEffect dependency array allows us to accomplish this, however the eslint missing dependencies
            warning recommends adding any dependencies used in the useEffect to the deps array. Since cardNetwork
            & amount state values can change often, adding them to the dependency array will result in unintended
            functionality so disabling this warning in this case makes sense.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (isEqual(step, STEP_LANDING) && isEqual(stepByRoute, STEP_CARRIER)) {
            previousStep()
            return
        }

        if (stepByRoute !== step) {
            if (findIndex(steps, stepByRoute) <= findIndex(steps, step)) {
                setStep(stepByRoute)
            }
        }
        // eslint-disable-next-line
    }, [location.pathname])

    useEffect(() => {
        batch(() => {
            dispatch(setCacAvailableCardNetworks(availableCardNetworks))
            dispatch(
                setCacConfig({
                    baseConfig: buildACardConfig,
                    baseUrl: baseUrl,
                    siteName: siteName,
                    faqUrl: faqUrl,
                    carrierImageUrl: carrierImageUrl,
                    carrierImageWithCardUrl: carrierImageWithCardUrl,
                    imageGuidelinesUrl: imageGuidelinesUrl,
                    includeShipping: includeShipping,
                    availableShippingMethods: availableShippingMethods,
                })
            )
        })
    }, [
        dispatch,
        availableCardNetworks,
        buildACardConfig,
        baseUrl,
        siteName,
        faqUrl,
        carrierImageUrl,
        carrierImageWithCardUrl,
        imageGuidelinesUrl,
        includeShipping,
        availableShippingMethods,
    ])

    const previousStep = () => {
        // Previous will always be valid (or not exist)
        let previousStep = getPreviousStep()

        if (!previousStep) {
            previousStep = STEP_LANDING
        }

        setStep(previousStep)
        history.push(previousStep.route)
    }

    const resetApp = useCallback(
        (resetCards, occasionSlug) => {
            // Reset card builder properties
            setStep(STEP_LANDING)

            if (resetCards === true) {
                // Remove saved cards
                dispatch(resetCacApp())
            } else {
                dispatch(resetCustomCard())
            }

            // Send back to landing page
            isNil(occasionSlug)
                ? history.push(STEP_LANDING.route)
                : history.push(`${STEP_LANDING.route}${occasionSlug}/`)
        },
        [history, dispatch, setStep]
    )

    const resetForm = useCallback(() => {
        dispatch(resetCustomCard())
    }, [dispatch])

    const saveCards = useCallback(
        (data) => {
            dispatch(saveCustomCards(data))
        },
        [dispatch]
    )

    const resetCardList = useCallback(() => {
        saveCards([])
    }, [saveCards])

    const findCard = useCallback(
        (cardId) => {
            return cards.find((card) => card.cardId === cardId)
        },
        [cards]
    )

    const updateCard = useCallback(
        (card) => {
            const newCards = cards.map((item) => {
                if (item.cardId === card.cardId) {
                    return card
                }
                return item
            })
            saveCards(newCards)
        },
        [cards, saveCards]
    )

    const editCard = useCallback(
        (cardId, step) => {
            const card = findCard(cardId)
            console.log('findCard', card)
            // Set the step we are on
            setStep(step)
            // Set card builder properties to selected card
            dispatch(presetCustomCard(card))
            // Send to landing page
            history.push(step.route)
        },
        [dispatch, setStep, history, findCard]
    )

    const nextStep = useCallback(() => {
        if (!step) setStep(STEP_LANDING)
        const nextStep = getNextStep()

        if (!nextStep) return

        if (isEmpty(customCard.cardNetwork)) {
            handleUpdateCustomCard('cardNetwork', cardNetwork)
        }

        if (nextStep === STEP_REVIEW) {
            console.log('review: add card to list', customCard)
            // If next step is review, add the current card state to array of cards
            const existingCard = findCard(customCard.cardId)
            if (existingCard) {
                console.log('update card')
                // we are editing a card
                updateCard(customCard)
            } else {
                console.log('add new card')
                pushBuildACardAddGiftToCartToDataLayer(
                    customCard.cardNetwork.name,
                    customCard.amount,
                    customCard.quantity
                )
                let cardPayload = []
                const cardQuantity = customCard.quantity
                for (let counter = 0; counter < cardQuantity; counter++) {
                    cardPayload.push({
                        ...customCard,
                        quantity: 1,
                        cardId: uuidv4(),
                    })
                }

                dispatch(addCustomCard(cardPayload))
            }
        }

        setStep(nextStep)
        history.push(nextStep.route)
    }, [
        customCard,
        getNextStep,
        history,
        setStep,
        step,
        findCard,
        cardNetwork,
        dispatch,
        handleUpdateCustomCard,
        updateCard,
    ])

    const handleSetCartErrors = useCallback(
        (errors) => {
            dispatch(setCartErrors(errors))
        },
        [dispatch]
    )
    const handleCartError = useCallback(
        (error) => {
            addToCartErrorHandler(error, handleSetCartErrors)
        },
        [handleSetCartErrors, addToCartErrorHandler]
    )

    const handleSetAddingToCart = useCallback(
        (addingToCart) => {
            dispatch(setAddingToCart(addingToCart))
        },
        [dispatch]
    )

    const updateAddressFromUspsAndValidate = useCallback(
        (response) => {
            if (typeof response.Address !== undefined) {
                if (response.Address.Address2) {
                    handleUpdateCustomCard(
                        'addressLine1',
                        response.Address.Address2
                    )
                }
                if (response.Address.City) {
                    handleUpdateCustomCard('city', response.Address.City)
                }
                if (response.Address.State) {
                    handleUpdateCustomCard('state', response.Address.State)
                }
                if (response.Address.Zip5) {
                    handleUpdateCustomCard('zip', response.Address.Zip5)
                }
            }

            const addressResult = handleAddressVerificationResponse(response)
            console.log('addressResult = ', addressResult)
            dispatch(putCustomCardAddressValidationResult(addressResult))

            return addressResult
        },
        [dispatch, handleUpdateCustomCard]
    )

    useEffect(() => {
        setCartErrors([])
        window.scrollTo(0, 0)
    }, [location])

    useEffect(() => {
        if (nextStepRequested) {
            nextStep()
            dispatch(requestNextStep(false))
        }
    }, [nextStepRequested, dispatch, nextStep])

    useEffect(() => {
        if (resetAppRequested) {
            dispatch(clearResetCacAppRequest())
            resetApp(clearCards, occasionSlug)
        }
    }, [resetAppRequested, dispatch, clearCards, resetApp, occasionSlug])

    useEffect(() => {
        async function sendAddToCartRequest(formData) {
            return await axios.post(addToCartUrl, formData, {
                headers: {
                    'Content-Type': 'application/json',
                },
            })
        }

        const handleAddToCart = () => {
            handleSetCartErrors([])
            addToCart(
                imageUploadUrl,
                cards,
                handleSetAddingToCart,
                resetForm,
                resetCardList,
                sendAddToCartRequest,
                addToCartCallback,
                handleCartError
            )
        }

        if (addToCartRequested) {
            handleAddToCart()
            dispatch(addToCartRequest(false))
        }
    }, [
        addToCartRequested,
        dispatch,
        cards,
        addToCart,
        handleSetAddingToCart,
        resetForm,
        resetCardList,
        addToCartCallback,
        addToCartUrl,
        handleCartError,
        handleSetCartErrors,
        imageUploadUrl,
    ])

    useEffect(() => {
        if (!isEmpty(editCardRequest)) {
            editCard(editCardRequest.cardId, editCardRequest.step)
            dispatch(resetEditCustomCardRequest())
        }
    }, [editCardRequest, dispatch, editCard])

    useEffect(() => {
        const removeCard = (cardId) => {
            const newCardList = cards.filter((item) => item.cardId !== cardId)
            saveCards(newCardList)
        }

        if (removeCardId) {
            removeCard(removeCardId)
            dispatch(removeCustomCardRequest(null))
        }
    }, [removeCardId, dispatch, cards, saveCards])

    useEffect(() => {
        const duplicateCard = (cardId) => {
            const cardToDuplicate = findCard(cardId)
            if (cardToDuplicate) {
                const card = Object.assign({}, cardToDuplicate, {
                    cardId: uuidv4(),
                })
                console.log('addCustomCard', card)
                dispatch(addCustomCard(card))
            }
        }

        if (duplicateCardId) {
            duplicateCard(duplicateCardId)
            dispatch(duplicateCustomCardRequest(null))
        }
    }, [duplicateCardId, dispatch, findCard])

    useEffect(() => {
        if (!isEmpty(addressValidationResponse)) {
            console.log('addressValidationResponse', addressValidationResponse)
            updateAddressFromUspsAndValidate(addressValidationResponse)
        }
    }, [addressValidationResponse, updateAddressFromUspsAndValidate])

    return !isEmpty(buildACardConfig) ? (
        <>
            <MuiThemeProvider theme={createACardTheme}>
                <AppHelmet
                    pageTitle={
                        isEqual(step, STEP_LANDING)
                            ? buildACardConfig.page_meta_title
                            : step.pageTitle
                    }
                    pageDescription={
                        isEqual(step, STEP_LANDING)
                            ? buildACardConfig.page_meta_description
                            : step.pageDescription
                    }
                />
                <CssBaseline />
                <Layout
                    baseUrl={baseUrl}
                    faqUrl={faqUrl}
                    brandLogoUrl={brandLogoUrl}
                    footerContent={footerContent}
                    useCreateACardFooter={!isEqual(step, STEP_LANDING)}
                >
                    {children}
                </Layout>
            </MuiThemeProvider>
        </>
    ) : null
}

export default CreateACard
